// src/routes.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useUser } from "hooks/useUser";

// Layouts
import MainLayout from "layouts/MainLayout/MainLayout";

// Páginas
import AuthPage from "pages/AuthPage/AuthPage";
import DashboardPage from "pages/DashboardPage/DashboardPage";
import NotFoundPage from "pages/NotFoundPage/NotFoundPage";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import AdminPage from "pages/AdminPage/AdminPage";
import DashboardReportsPage from "pages/DashboardReportsPage/DashboardReportsPage";
import EstoquePage from "pages/EstoquePage/EstoquePage";
import FechamentoFiscalPage from "pages/FechamentoFiscalPage/FechamentoFiscalPage";
import ClientsTab from "features/clients/ClientsTab";
import CreditsTab from "features/credits/CreditsTab";
import GuiasTab from "features/guias/GuiasTab";

import { Lock } from "lucide-react";

const PendingApprovalPage = () => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white shadow-xl rounded-2xl p-8 text-center border border-gray-100">
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-orange-100 rounded-full">
            <Lock className="w-8 h-8 text-orange-600" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          Acesso Restrito
        </h1>
        <p className="text-gray-600 mb-6 leading-relaxed">
          Este portal é destinado exclusivamente ao uso interno e controle do{" "}
          <span className="font-semibold text-gray-900">Ecoparque Timbó</span>.
        </p>
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 text-sm text-orange-800">
          Por favor, consulte o administrador do sistema para autorizar o seu
          acesso.
        </div>
      </div>
    </div>
  );
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, role } = useUser();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!role || role === "pending")
    return <Navigate to="/pending-approval" replace />;

  if (isAdmin) {
    return children;
  }

  return <Navigate to="/" replace />;
};

const PermissionRoute = ({ children, permission }) => {
  const { isAuthenticated } = useAuth();
  const userContext = useUser();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!userContext.role || userContext.role === "pending")
    return <Navigate to="/pending-approval" replace />;

  const hasPermission = userContext[permission];

  if (hasPermission) {
    return children;
  }

  return <Navigate to="/" replace />;
};

const AppRoutes = () => {
  const { isAuthenticated, loading: loadingAuth } = useAuth();
  const { role, isLoadingUser } = useUser();

  if (loadingAuth || isLoadingUser) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <Routes>
        {/* Rota de Login */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              !role || role === "pending" ? (
                <Navigate to="/pending-approval" replace />
              ) : (
                <Navigate to="/" replace />
              )
            ) : (
              <AuthPage />
            )
          }
        />

        {/* Rota de "Aguardando Aprovação" */}
        <Route
          path="/pending-approval"
          element={
            isAuthenticated && (!role || role === "pending") ? (
              <PendingApprovalPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* Rota principal */}
        <Route
          path="/"
          element={
            !isAuthenticated ? (
              <Navigate to="/login" replace />
            ) : !role || role === "pending" ? (
              <Navigate to="/pending-approval" replace />
            ) : (
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            )
          }
        />

        {/* Rota para Clientes */}
        <Route
          path="/clientes"
          element={
            !isAuthenticated ? (
              <Navigate to="/login" replace />
            ) : !role || role === "pending" ? (
              <Navigate to="/pending-approval" replace />
            ) : (
              <AdminRoute>
                <MainLayout>
                  <ClientsTab />
                </MainLayout>
              </AdminRoute>
            )
          }
        />

        {/* Rota para Estoque */}
        <Route
          path="/estoque"
          element={
            <PermissionRoute permission="canViewEstoque">
              <MainLayout>
                <EstoquePage />
              </MainLayout>
            </PermissionRoute>
          }
        />

        {/* Rota para Fechamento Fiscal (Nova Calculadora) */}
        <Route
          path="/fiscal"
          element={
            <AdminRoute>
              <MainLayout>
                <FechamentoFiscalPage />
              </MainLayout>
            </AdminRoute>
          }
        />

        {/* Rota para Guias */}
        <Route
          path="/guias"
          element={
            <PermissionRoute permission="canViewGuias">
              <MainLayout>
                <GuiasTab />
              </MainLayout>
            </PermissionRoute>
          }
        />

        {/* Rota para o Painel de Admin */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <MainLayout>
                <AdminPage />
              </MainLayout>
            </AdminRoute>
          }
        />

        {/* Rota para o Dashboard de Relatórios */}
        <Route
          path="/resumo"
          element={
            <PermissionRoute permission="canViewDashboard">
              <MainLayout>
                <DashboardReportsPage />
              </MainLayout>
            </PermissionRoute>
          }
        />

        {/* Rota 404 para qualquer outro caminho */}
        <Route path="*" element={<NotFoundPage />} />

        {/* Rota de Créditos */}
        <Route
          path="/creditos"
          element={
            <AdminRoute>
              <MainLayout>
                <CreditsTab />
              </MainLayout>
            </AdminRoute>
          }
        />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
